"use client";

import { useState } from "react";
import {
  Bar,
  BarChart,
  Area,
  AreaChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import useSWR from "swr";
import { useRollingMonthlyUsage } from "@/hooks/use-rolling-monthly-usage";
import { UsageUnavailable } from "@/components/usage-unavailable";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getDailyStatisticsLast30Days } from "@/services/statistics.service";

interface ChartDataPoint {
  month: string;
  value: number;
  users?: number;
}

interface FeatureChartProps {
  featureKey:
    | "youzuLens"
    | "roomVisualizer"
    | "propertyVisualize"
    | "twoDTo3D"
    | "swap"
    | "magicBackground";
  usersKey?:
    | "youzuLensUsers"
    | "roomVisualizerUsers"
    | "propertyVisualizeUsers"
    | "twoDTo3DUsers"
    | "swapUsers"
    | "magicBackgroundUsers";
  dataLabel: string;
  color: string;
  showUsers?: boolean;
}

const monthNames = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export function FeatureChart({
  featureKey,
  usersKey,
  dataLabel,
  color,
  showUsers = false,
}: FeatureChartProps) {
  const [viewMode, setViewMode] = useState<"monthly" | "daily">("daily");

  const { data: monthlyData, isLoading: monthlyLoading, isUnavailable: monthlyUnavailable } =
    useRollingMonthlyUsage(viewMode === "monthly");

  const { data: dailyResponse, error: dailyError, isLoading: dailyLoading } = useSWR(
    viewMode === "daily" ? "daily-stats-last-30-days" : null,
    getDailyStatisticsLast30Days,
    { refreshInterval: 60000 },
  );

  const isUnavailable = viewMode === "daily"
    ? Boolean(dailyError) || dailyResponse?.success === false
    : monthlyUnavailable;
  const isLoading = viewMode === "monthly"
    ? monthlyLoading
    : dailyLoading || !dailyResponse;

  const getChartData = (): ChartDataPoint[] => {
    if (viewMode === "daily") {
      if (!dailyResponse?.success || !dailyResponse.data.length) return [];
      return dailyResponse.data.map((stat) => {
        const parts = stat.period.split("-");
        const monthIndex = parseInt(parts[1]) - 1;
        const day = parseInt(parts[2]);
        return {
          month: `${monthNames[monthIndex]} ${day}`,
          value: stat[featureKey] ?? 0,
          users: usersKey ? stat[usersKey] ?? 0 : undefined,
        };
      });
    }

    return monthlyData.map(({ name, stats }) => ({
      month: name,
      value: stats?.[featureKey] ?? 0,
      users: usersKey ? stats?.[usersKey] ?? 0 : undefined,
    }));
  };

  const renderContent = () => {
    if (isUnavailable) {
      return (
        <div className="h-[300px] w-full flex items-center justify-center">
          <UsageUnavailable className="text-center" />
        </div>
      );
    }
    if (isLoading) {
      return (
        <div className="h-[300px] w-full flex items-center justify-center">
          <div className="text-muted-foreground">Loading chart data...</div>
        </div>
      );
    }

    const data = getChartData();
    if (data.length === 0) {
      return (
        <div className="h-[300px] w-full flex items-center justify-center">
          <div className="text-muted-foreground">No data available</div>
        </div>
      );
    }
    return viewMode === "daily" ? (
      <div className="h-[300px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id={`colorFeature-${featureKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
              {showUsers && (
                <linearGradient id={`colorUsers-${featureKey}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4CC9F0" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#4CC9F0" stopOpacity={0} />
                </linearGradient>
              )}
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fill: "#666" }}
              tickMargin={10}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 12, fill: "#666" }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip
              contentStyle={{
                borderRadius: "8px",
                border: "none",
                boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
              }}
            />
            <Legend iconType="circle" wrapperStyle={{ paddingTop: "20px" }} />
            <Area
              type="monotone"
              dataKey="value"
              name={dataLabel}
              stroke={color}
              strokeWidth={2}
              fillOpacity={1}
              fill={`url(#colorFeature-${featureKey})`}
            />
            {showUsers && usersKey && (
              <Area
                type="monotone"
                dataKey="users"
                name="Unique Users"
                stroke="#4CC9F0"
                strokeWidth={2}
                strokeDasharray="5 5"
                fillOpacity={1}
                fill={`url(#colorUsers-${featureKey})`}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    ) : (
      <div className="h-[300px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fill: "#666" }}
              tickMargin={10}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 12, fill: "#666" }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip
              contentStyle={{
                borderRadius: "8px",
                border: "none",
                boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
              }}
            />
            <Legend iconType="circle" wrapperStyle={{ paddingTop: "20px" }} />
            <Bar
              dataKey="value"
              name={dataLabel}
              fill={color}
              radius={[4, 4, 0, 0]}
              maxBarSize={60}
            />
            {showUsers && usersKey && (
              <Bar
                dataKey="users"
                name="Unique Users"
                fill="#4CC9F0"
                radius={[4, 4, 0, 0]}
                maxBarSize={60}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="flex justify-end mb-2">
        <Tabs
          value={viewMode}
          onValueChange={(v) => setViewMode(v as "monthly" | "daily")}
        >
          <TabsList>
            <TabsTrigger value="daily">Daily</TabsTrigger>
            <TabsTrigger value="monthly">Monthly</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {renderContent()}
    </div>
  );
}
